import { DefaultPageSize, Link, ListResource, Paging, Resource } from '@/shared/models';
import { defineStore } from 'pinia';
import {
    Message,
    MessageCategoryTemplate,
    MessageListGroupItem,
    MessageListItem,
    MessageScope,
    MessageSource,
    MessageSourceLink,
    MessageTemplate,
    ServiceJourney
} from './messagesModels';
import { MessagesRels } from './rels';
import { useCommonStore } from '@/shared/commonStore';
import { useAxios } from '@/shared/axios';
import { getLink } from '@/shared/LinkHelper';
import _ from 'lodash';

const emptyMessage = {
    subject: '',
    body: '',
    pushNotifications: null as boolean | null,
    channels: ['General'],
    startPublish: null,
    endPublish: null,
    validFrom: null,
    receivers: {
        scopes: [],
        lines: [],
        stops: [],
        areas: [],
        companies: [],
        drivers: [],
        departures: []
    },
    structuredMessage: {
        templateId: '',
        input: {},
        choice: {}
    }
} as unknown as Message;

export const useMessagesStore = defineStore('deviation-messages', {
    state: () => ({
        messages: new Array<MessageListItem>(),
        groups: new Array<MessageListGroupItem>(),
        selectedGroup: null as MessageListGroupItem | null,
        message: _.cloneDeep(emptyMessage),
        paging: {} as Paging,
        source: MessageSource.AvvikPortal,
        sourceLinks: new Array<MessageSourceLink>(),
        query: '',
        filters: {
            status: '',
            lineName: '',
            companyNumber: '',
            categoryName: ''
        },
        categoryFilterItems: new Array<MessageCategoryTemplate>(),
        categoryTemplates: new Array<MessageCategoryTemplate>(),
        messageTemplates: new Array<MessageTemplate>(),
        serviceJourneys: new Array<ServiceJourney>(),
        links: new Array<Link>(),
        messageLinks: new Array<Link>()
    }),
    getters: {
        currentMessageTemplate: state => {
            if (!state.message.structuredMessage?.templateId) return null;
            return state.messageTemplates.find(x => state.message.structuredMessage?.templateId === x.id);
        }
    },
    actions: {
        async loadMessages(page: number, id?: string, groupId?: string) {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MessagesRels.getMessages);

            const pageSize = DefaultPageSize;
            const requestParams = new URLSearchParams();
            requestParams.append('page.number', page.toString());
            requestParams.append('page.size', pageSize.toString());
            requestParams.append('source', this.source);
            requestParams.append('status', this.filters.status);
            requestParams.append('lineName', this.filters.lineName);
            requestParams.append('companyNumber', this.filters.companyNumber);
            requestParams.append('categoryName', this.filters.categoryName);

            if (this.query) {
                requestParams.append('query', this.query);
            }

            if (id) {
                requestParams.append('id', id.toString());
            }

            if (groupId) {
                requestParams.append('groupId', groupId.toString());
            }

            const response = await axios.requestByLink<ListResource<MessageListItem>>(link, { params: requestParams });

            this.messages = response.data.data;
            this.links = response.data.links;
            this.paging = response.data.paging ?? ({} as Paging);
            this.loadSourceLinks();
        },
        async loadMessageGroups(page: number) {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MessagesRels.getMessageGroups);

            const pageSize = DefaultPageSize;
            const requestParams = new URLSearchParams();
            requestParams.append('page.number', page.toString());
            requestParams.append('page.size', pageSize.toString());
            requestParams.append('source', this.source);
            requestParams.append('status', this.filters.status);
            requestParams.append('lineName', this.filters.lineName);
            requestParams.append('companyNumber', this.filters.companyNumber);
            requestParams.append('categoryName', this.filters.categoryName);

            if (this.query) {
                requestParams.append('query', this.query);
            }

            const response = await axios.requestByLink<ListResource<MessageListGroupItem>>(link, { params: requestParams });

            this.groups = response.data.data;
            this.messages = response.data.data.flatMap(g => g.messages);
            this.links = response.data.links;
            this.paging = response.data.paging ?? ({} as Paging);
            this.loadSourceLinks();
        },
        loadGroup(groupId: string) {
            this.selectedGroup = this.groups.find(g => g.groupId === groupId) ?? null;
        },
        async unpublishGroup() {
            if (!this.selectedGroup) return;
            const axios = useAxios();
            const link = getLink(MessagesRels.unpublishMessageGroup, this.selectedGroup.links);
            if (!link) return;
            await axios.requestByLink(link, {
                method: link.method ?? 'POST',
                data: { groupId: this.selectedGroup.groupId }
            });
        },
        async loadMessage(id: string) {
            const axios = useAxios();

            let item = this.getMessageById(id);
            if (!item) {
                // fallback if message is not in the list
                await this.loadMessages(1, id);
                item = this.getMessageById(id);
                if (!item) throw Error(`Can't find message by id: ${id}`);
            }

            const link = getLink(MessagesRels.getMessage, item.links);

            const response = await axios.requestByLink<Resource<Message>>(link);

            this.message = response.data.data ?? _.cloneDeep(emptyMessage);
            this.messageLinks = response.data.links;
        },
        async initMessage() {
            await this.loadMessages(1);
            this.message = _.cloneDeep(emptyMessage);
        },
        async createMessage() {
            const axios = useAxios();
            const link = getLink(MessagesRels.createMessage, this.links);

            const response = await axios.requestByLink<{ groupId: string }>(link, { data: this.message });
            const groupId = response.data.groupId;
            await this.loadMessages(1, undefined, groupId);
            return groupId;
        },
        async updateMessage() {
            const axios = useAxios();
            const link = getLink(MessagesRels.updateMessage, this.messageLinks);

            await axios.requestByLink(link, { data: this.message });
        },
        async publishMessage() {
            const axios = useAxios();
            const link = getLink(MessagesRels.publishMessage, this.messageLinks);

            await axios.requestByLink(link);
        },
        async unpublishMessage() {
            const axios = useAxios();
            const link = getLink(MessagesRels.unpublishMessage, this.messageLinks);

            await axios.requestByLink(link);
        },
        async publishMessageGroup(groupId: string) {
            const axios = useAxios();
            const link = getLink(MessagesRels.publishMessageGroup, this.links);

            await axios.requestByLink(link, { data: { groupId: groupId } });
        },
        async loadMessageCategoriesBySource() {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MessagesRels.getMessageCategories);

            const requestParams = new URLSearchParams();
            requestParams.append('source', this.source);

            const response = await axios.requestByLink<ListResource<MessageCategoryTemplate>>(link, { params: requestParams });
            this.categoryFilterItems = response.data.data;
        },
        async loadMessageCategories() {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MessagesRels.getMessageCategories);

            const response = await axios.requestByLink<ListResource<MessageCategoryTemplate>>(link);
            this.categoryTemplates = response.data.data;
        },
        async loadMessageTemplates() {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MessagesRels.getMessageTemplates);

            const response = await axios.requestByLink<ListResource<MessageTemplate>>(link);
            this.messageTemplates = response.data.data;
        },
        async loadServiceJourneys({ date, lineNumber }: { date: string; lineNumber: string }) {
            const commonStore = useCommonStore();
            const axios = useAxios();
            const link = commonStore.getLink(MessagesRels.getServiceJourneys);

            const requestParams = new URLSearchParams();
            requestParams.append('calendarDay', date);
            requestParams.append('lineNumber', lineNumber);

            const response = await axios.requestByLink<ListResource<ServiceJourney>>(link, { params: requestParams });

            this.serviceJourneys = response.data.data;
        },
        loadSourceLinks() {
            if (this.sourceLinks.length) return;

            this.sourceLinks = this.links
                .filter(x => x.rel.includes('source:'))
                .map(y => {
                    const titleChunks = y.rel.split(':');
                    const titleValue = _.upperFirst(titleChunks[1]);
                    const titleTranslationKey = titleChunks[0] + titleValue;

                    return {
                        href: y.href,
                        source: titleValue as MessageSource,
                        translationKey: titleTranslationKey
                    };
                });
        },
        getMessageById(id: string) {
            return this.messages.find(message => message.id === id);
        },
        getMessageTemplateById(id: string) {
            return this.messageTemplates.find(template => template.id === id);
        },
        getMessageTemplatesByCategory(categoryName: string) {
            return this.messageTemplates.filter(x => x.categoryName == categoryName);
        },
        getScopesFromMessageTemplates(templates: Array<MessageTemplate>) {
            return _.chain(templates).flatMap('messageScopes').uniq().value();
        },
        filterMessageTemplatesByScope(templates: Array<MessageTemplate>, scope?: MessageScope) {
            return templates.filter(x => (scope ? x.messageScopes.includes(scope) : false));
        }
    }
});
